/**
 * Document represents a stored file in the system and maps to a FHIR DocumentReference.
 * It provides metadata and accessors for uploaded documents linked to Binary content.
 */

import type { MedplumClient } from "@medplum/core";
import type {
  Attachment,
  Bundle,
  DocumentReference,
  Extension as FhirExtension,
  Identifier,
} from "@medplum/fhirtypes";
import { DocumentExtension, ParserOutcomePolicyVersion } from "@vibrant/models";
import {
  type CreateResourceOptions,
  FhirResource,
  type SearchOptions,
  type SubscriptionEventMetadata,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceRef, SystemValuePair } from "@vibrant/models/fhir/fhir-resource-types";
import { z } from "zod";
import { haveSameUniqueValues, uniqueSorted } from "@/lib/arrays";
import { isMedplumResourceMissingError } from "@/lib/errors/is-medplum-resource-missing-error";
import {
  type DocumentCategoryCode,
  getDocumentCategoryCode,
  getDocumentCategoryLabel,
} from "@/lib/fhir/document-categories";
import FhirExtensionValues from "@/lib/fhir/Extension";
import { resolveBinaryIdFromUrl } from "@/lib/files/utils";
import { createLogger } from "@/lib/logger";
import { extractTelemetryErrorMetadata } from "@/lib/telemetry-utils";
import { parseDate, time } from "@/lib/time";
import {
  type CreateDocumentFromFileOptions,
  type CreateDocumentFromFileRequest,
  type CreateDocumentFromFileResult,
  createDocumentFromFile,
  withDocumentReferenceSortPeriod,
} from "./document-file-write";

import {
  type CreateDocumentFromBinaryRequest,
  type CreateDocumentFromBinaryResult,
  createDocumentFromUploadedBinary,
} from "./document-upload-write";
import { MedplumFile } from "./MedplumFile";

const logger = createLogger("models/files/Document");

/**
 * Re-export individual URL constants from the shared package for backward
 * compatibility — existing web imports of these named constants keep working.
 */
export const PARSING_STATUS_EXTENSION_URL = DocumentExtension.ParsingStatus;
export const PARSER_SESSION_ID_EXTENSION_URL = DocumentExtension.ParserSessionId;
export const EXPECTED_PARSER_TYPES_EXTENSION_URL = DocumentExtension.ExpectedParserTypes;
export const COMPLETED_PARSER_TYPES_EXTENSION_URL = DocumentExtension.CompletedParserTypes;
export const FAILED_PARSER_TYPES_EXTENSION_URL = DocumentExtension.FailedParserTypes;
export const PARSER_OUTCOME_SUMMARY_EXTENSION_URL = DocumentExtension.ParserOutcomeSummary;
export const PARSING_FAILURE_REASON_EXTENSION_URL = DocumentExtension.ParsingFailureReason;
export const PARSING_HOLD_REASON_EXTENSION_URL = DocumentExtension.ParsingHoldReason;
export const EFFECTIVE_DATE_EXTENSION_URL = DocumentExtension.EffectiveDate;
export const EFFECTIVE_DATE_SOURCE_EXTENSION_URL = DocumentExtension.EffectiveDateSource;
export const SOURCE_EXTENSION_URL = DocumentExtension.Source;
export const CLASSIFICATION_TYPES_EXTENSION_URL = DocumentExtension.ClassificationTypes;
export const MANUAL_CATEGORY_CODE_EXTENSION_URL = DocumentExtension.ManualCategoryCode;

// Classifier writes this literal to the source extension when it finds no source - read it as absent.
export const NO_SOURCE_PLACEHOLDER = "No source";

// Parsing status values. `queued`: the parse session waits for the harness to leave the Inngest queue.
export type ParsingStatus =
  | "pending"
  | "queued"
  | "processing"
  // a lab envelope waits off the chart for the quality gate or an operator
  | "held"
  // nobody has asked for this document to be parsed. Distinct from `pending`, which
  // means a parse is coming: a deferred document waits for a person, not for a queue.
  | "deferred"
  | "completed"
  | "failed"
  | "not-started";

// Structured reason codes for why parsing failed — queryable at the app level
// without log spelunking. Stored on the DocumentReference as an extension.
export const PARSING_FAILURE_REASONS = [
  "unsupported_file_type",
  "empty_document",
  "document_gone",
  "unsupported_mime_type",
  // Over the model's input-token or per-file size cap; a retry sends the same bytes.
  "document_too_large",
  // A Word `.docx` that mammoth cannot convert to HTML (corrupt or encrypted). A retry reads the same bytes.
  "docx_conversion_failed",
  "parser_error",
  "retries_exhausted",
  "stuck_timeout",
  // The harness never left the queue within the watchdog's queue backstop.
  "queue_timeout",
  "never_enqueued",
  "unknown",
] as const;
export type ParsingFailureReason = (typeof PARSING_FAILURE_REASONS)[number];
const PARSING_FAILURE_REASON_SET: ReadonlySet<string> = new Set(PARSING_FAILURE_REASONS);

export const PARSED_FROM_DOCUMENT_REFERENCE = "parsed-from-document-reference";

export type EffectiveDateSource = "manual" | "parser";
const EFFECTIVE_DATE_SOURCES = new Set<string>(["manual", "parser"]);

export const PARSER_OUTCOME_SUMMARY_POLICY_VERSION = ParserOutcomePolicyVersion;

export const ParserOutcomeSummarySchema = z.object({
  policyVersion: z.literal(PARSER_OUTCOME_SUMMARY_POLICY_VERSION),
  parseSessionId: z.string(),
  outcomeKind: z.enum(["completed", "completed_with_non_blocking_failures", "failed"]),
  parsingStatus: z.enum(["completed", "failed"]),
  policyReason: z.string(),
  expectedParserTypes: z.array(z.string()),
  completedParserTypes: z.array(z.string()),
  failedParserTypes: z.array(z.string()),
  blockingFailedParserTypes: z.array(z.string()),
  nonBlockingFailedParserTypes: z.array(z.string()),
  primaryCompletedParserTypes: z.array(z.string()),
});
export type ParserOutcomeSummary = z.infer<typeof ParserOutcomeSummarySchema>;

export function serializeParserOutcomeSummary(summary: ParserOutcomeSummary): string {
  return JSON.stringify({
    ...summary,
    expectedParserTypes: uniqueSorted(summary.expectedParserTypes),
    completedParserTypes: uniqueSorted(summary.completedParserTypes),
    failedParserTypes: uniqueSorted(summary.failedParserTypes),
    blockingFailedParserTypes: uniqueSorted(summary.blockingFailedParserTypes),
    nonBlockingFailedParserTypes: uniqueSorted(summary.nonBlockingFailedParserTypes),
    primaryCompletedParserTypes: uniqueSorted(summary.primaryCompletedParserTypes),
  });
}

export function parseParserOutcomeSummary(
  value: string | undefined,
): ParserOutcomeSummary | undefined {
  if (!value) {
    return undefined;
  }

  try {
    return ParserOutcomeSummarySchema.parse(JSON.parse(value));
  } catch {
    return undefined;
  }
}

function isEffectiveDateSource(value: string | undefined): value is EffectiveDateSource {
  return value !== undefined && EFFECTIVE_DATE_SOURCES.has(value);
}

export interface ExternalRecordDisplayDate {
  value: string | undefined;
  source: "service" | "uploaded" | "unknown";
}

type DocumentServiceDateExtension = Omit<FhirExtension, "valueString"> & {
  valueString?: string | null;
};

interface DocumentServiceDateFields {
  extension: FhirExtension[];
  context?: DocumentReference["context"];
}

interface DocumentServiceDatePatchFields {
  extension: DocumentServiceDateExtension[];
  context?: DocumentReference["context"];
}

interface DocumentData {
  status?: DocumentReference["status"];
  docStatus?: DocumentReference["docStatus"];
  description: string;
  attachments: {
    contentType: string;
    url: string;
    title: string;
    creation?: string;
  }[];
  identifiers?: SystemValuePair[];
  date?: string;
  subject?: ResourceRef;
  custodian?: ResourceRef;
  /** Optional authorship attribution for the document (e.g., originating lab/vendor org). */
  author?: DocumentReference["author"];
  extension?: FhirExtension[];
  type?: DocumentReference["type"];
  context?: DocumentReference["context"];
  meta?: DocumentReference["meta"];
  category?: DocumentReference["category"];
}

/**
 * Maps classifier / extension effective-date text to the UTC instant used for `context.period.start`,
 * falling back to `DocumentReference.date` when the extension is missing or placeholder ("No date").
 */
export function effectiveDateExtensionToSortInstant(
  effectiveDateExtension: string | undefined,
  fallbackDate: string | undefined,
): string | undefined {
  if (effectiveDateExtension && effectiveDateExtension !== "No date") {
    try {
      return normalizeServiceDateToUtcIso(effectiveDateExtension);
    } catch {
      return fallbackDate;
    }
  }
  return fallbackDate;
}

/**
 * Converts a customer-facing service date into the canonical chronology instant.
 */
export function normalizeServiceDateToUtcIso(serviceDate: Date | string): string {
  if (serviceDate instanceof Date) {
    const year = serviceDate.getFullYear();
    const month = String(serviceDate.getMonth() + 1).padStart(2, "0");
    const day = String(serviceDate.getDate()).padStart(2, "0");
    return normalizeDateOnlyToUtcIso(`${year}-${month}-${day}`);
  }

  const trimmed = serviceDate.trim();
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : trimmed.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    return normalizeDateOnlyToUtcIso(dateOnly);
  }

  const parsed = time(trimmed);
  if (!parsed.isValid()) {
    throw new Error("Invalid service date");
  }

  return normalizeDateOnlyToUtcIso(parsed.format("YYYY-MM-DD"));
}

/**
 * Builds the coupled service-date extensions and chronology period.
 */
export function buildDocumentServiceDateFields({
  serviceDate,
  source,
  context,
}: {
  serviceDate: Date | string;
  source: EffectiveDateSource;
  context?: DocumentReference["context"];
}): DocumentServiceDateFields {
  const serviceDateIso = normalizeServiceDateToUtcIso(serviceDate);

  return {
    extension: [
      { url: EFFECTIVE_DATE_EXTENSION_URL, valueString: serviceDateIso },
      { url: EFFECTIVE_DATE_SOURCE_EXTENSION_URL, valueString: source },
    ],
    context: buildContextWithPeriodStart({
      context,
      periodStart: serviceDateIso,
    }),
  };
}

/**
 * Builds a patch that clears the semantic service date while preserving chronology fallback.
 */
export function buildDocumentServiceDateClearPatch({
  context,
  fallbackDate,
}: {
  context?: DocumentReference["context"];
  fallbackDate?: string;
}): DocumentServiceDatePatchFields {
  return {
    extension: [
      { url: EFFECTIVE_DATE_EXTENSION_URL, valueString: null },
      { url: EFFECTIVE_DATE_SOURCE_EXTENSION_URL, valueString: null },
    ],
    context: buildContextWithPeriodStart({
      context,
      periodStart: fallbackDate,
    }),
  };
}

/**
 * Resolves the user-facing External Records chronology date without confusing upload fallbacks
 * for true clinical service dates.
 */
export function resolveExternalRecordDisplayDate(
  document: Pick<Document, "effectiveDate" | "date" | "metaLastUpdated">,
): ExternalRecordDisplayDate {
  if (document.effectiveDate) {
    return { value: document.effectiveDate, source: "service" };
  }

  const uploadedDate = document.date ?? document.metaLastUpdated;
  if (uploadedDate) {
    return { value: uploadedDate, source: "uploaded" };
  }

  return { value: undefined, source: "unknown" };
}

export function getDocumentExtensionStringValue({
  extension,
  url,
}: {
  extension: FhirExtension[] | undefined;
  url: string;
}): string | undefined {
  return extension?.find((ext) => ext.url === url)?.valueString;
}

export function parseDocumentExtensionStringList({
  extension,
  url,
}: {
  extension: FhirExtension[] | undefined;
  url: string;
}): string[] | undefined {
  const value = getDocumentExtensionStringValue({ extension, url });
  if (!value) {
    return undefined;
  }

  const items = value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  return items.length > 0 ? items : undefined;
}

interface DocumentCreateIfNoneExistParams {
  request: DocumentData;
  ifNoneExistQuery: string;
  options?: CreateResourceOptions;
}

/** The query `Document.search` and `Document.subscribe` share. `subscribe` reads `subject` only. */
interface DocumentSearchQuery {
  /** `Patient/<id>`. */
  subject?: string;
  ids?: string[];
}

// one `_id=a,b,c` search per chunk keeps the query string under the request-line limit
const MAX_IDS_PER_SEARCH = 100;

export class Document extends FhirResource<DocumentReference> {
  static readonly ResourceType = "DocumentReference" as const;

  static readonly Extension = DocumentExtension;

  static readonly Type = {
    LaboratoryProcedure: {
      coding: [
        {
          system: FhirExtensionValues.Codes.SNOMED,
          code: "108252007",
          display: "Laboratory procedure",
        },
      ],
    },
    LabLabels: {
      text: "Lab Labels",
    },
    CollectionInstructions: {
      text: "Collection Instructions",
    },
  } satisfies Record<
    "LaboratoryProcedure" | "LabLabels" | "CollectionInstructions",
    NonNullable<DocumentReference["type"]>
  >;

  //------------------------------------------------------------------------------------------------
  //                               VIRTUAL PROPERTIES
  //------------------------------------------------------------------------------------------------
  get description(): string | undefined {
    return this.resource.description;
  }

  get attachments(): Attachment[] {
    return this.resource.content?.map((content) => content.attachment) || [];
  }

  get memberId(): string | undefined {
    return this.resource.subject?.reference?.split("/")[1];
  }

  /**
   * The document's subject, but only when it actually references a Patient — the shape
   * `Communication.subject` wants. Don't build this from `memberId` above: that getter splits the
   * reference unconditionally, so a document subject-ed to anything else would mint a bogus
   * `Patient/<non-patient-id>`.
   */
  get patientSubject(): ResourceRef | undefined {
    const subject = this.resource.subject;
    const reference = subject?.reference;
    if (!reference?.startsWith("Patient/")) return undefined;

    // Rebuild rather than return `subject` directly: FHIR's `Reference.reference` is optional, so
    // only the narrowed local satisfies `ResourceRef`.
    return { ...subject, reference };
  }

  /**
   * Document chronology used for External Records sorting/display.
   * Prefer the materialized sort period and fall back to the deprecated extension during rollout.
   */
  get chronologyDate(): string | undefined {
    const periodStart = this.resource.context?.period?.start;
    if (periodStart) {
      return periodStart;
    }

    return this.effectiveDate;
  }

  /**
   * Semantic effective date from the deprecated classifier extension.
   * Keep this separate from `chronologyDate` so clinical readers do not inherit upload-time fallbacks.
   */
  get effectiveDate(): string | undefined {
    const extensionValue = getDocumentExtensionStringValue({
      extension: this.resource.extension,
      url: EFFECTIVE_DATE_EXTENSION_URL,
    });

    if (!extensionValue || extensionValue === "No date") {
      return undefined;
    }

    return extensionValue;
  }

  get effectiveDateSource(): EffectiveDateSource | undefined {
    const source = getDocumentExtensionStringValue({
      extension: this.resource.extension,
      url: EFFECTIVE_DATE_SOURCE_EXTENSION_URL,
    });

    if (!isEffectiveDateSource(source)) {
      return undefined;
    }

    return source;
  }

  get hasManualEffectiveDate(): boolean {
    return this.effectiveDateSource === "manual" && Boolean(this.effectiveDate);
  }

  /** Classifier source label. Undefined when unset or when the classifier found no source. */
  get source(): string | undefined {
    const extension = this.resource.extension?.find((ext) => ext.url === SOURCE_EXTENSION_URL);
    const source = extension?.valueString;
    return source === NO_SOURCE_PLACEHOLDER ? undefined : source;
  }

  /**
   * Get the raw classifier types associated with this document.
   *
   * This reflects what the classifier detected before routing/suppression rules trim the parser
   * fan-out set. Returns undefined if the document was uploaded before this metadata was stored.
   */
  get classificationTypes(): string[] | undefined {
    return parseDocumentExtensionStringList({
      extension: this.resource.extension,
      url: CLASSIFICATION_TYPES_EXTENSION_URL,
    });
  }

  get category(): DocumentReference["category"] | undefined {
    return this.resource.category;
  }

  get categoryCode(): DocumentCategoryCode | undefined {
    return getDocumentCategoryCode({ category: this.resource.category });
  }

  get categoryLabel(): string | undefined {
    return getDocumentCategoryLabel({ code: this.categoryCode });
  }

  get parsingStatus(): ParsingStatus {
    return (
      (getDocumentExtensionStringValue({
        extension: this.resource.extension,
        url: PARSING_STATUS_EXTENSION_URL,
      }) as ParsingStatus) || "not-started"
    );
  }

  /** Why a `held` document is held, once something stamped a reason. */
  get parsingHoldReason(): string | undefined {
    return getDocumentExtensionStringValue({
      extension: this.resource.extension,
      url: PARSING_HOLD_REASON_EXTENSION_URL,
    });
  }

  get parsingFailureReason(): ParsingFailureReason | undefined {
    const raw = getDocumentExtensionStringValue({
      extension: this.resource.extension,
      url: PARSING_FAILURE_REASON_EXTENSION_URL,
    });
    if (!raw) return undefined;
    return PARSING_FAILURE_REASON_SET.has(raw) ? (raw as ParsingFailureReason) : "unknown";
  }

  get parserSessionId(): string | undefined {
    return getDocumentExtensionStringValue({
      extension: this.resource.extension,
      url: PARSER_SESSION_ID_EXTENSION_URL,
    });
  }

  get expectedParserTypes(): string[] | undefined {
    return parseDocumentExtensionStringList({
      extension: this.resource.extension,
      url: EXPECTED_PARSER_TYPES_EXTENSION_URL,
    });
  }

  get completedParserTypes(): string[] | undefined {
    return parseDocumentExtensionStringList({
      extension: this.resource.extension,
      url: COMPLETED_PARSER_TYPES_EXTENSION_URL,
    });
  }

  get failedParserTypes(): string[] | undefined {
    return parseDocumentExtensionStringList({
      extension: this.resource.extension,
      url: FAILED_PARSER_TYPES_EXTENSION_URL,
    });
  }

  get parserOutcomeSummary(): ParserOutcomeSummary | undefined {
    const summary = parseParserOutcomeSummary(
      getDocumentExtensionStringValue({
        extension: this.resource.extension,
        url: PARSER_OUTCOME_SUMMARY_EXTENSION_URL,
      }),
    );
    if (!summary || summary.parseSessionId !== this.parserSessionId) {
      return undefined;
    }
    if (summary.parsingStatus !== this.parsingStatus) {
      return undefined;
    }

    const expectedParserTypes = this.expectedParserTypes ?? [];
    const completedParserTypes = this.completedParserTypes ?? [];
    const failedParserTypes = this.failedParserTypes ?? [];
    const hasMatchingTrackingState =
      haveSameUniqueValues(summary.expectedParserTypes, expectedParserTypes) &&
      haveSameUniqueValues(summary.completedParserTypes, completedParserTypes) &&
      haveSameUniqueValues(summary.failedParserTypes, failedParserTypes);
    if (!hasMatchingTrackingState) {
      return undefined;
    }

    return summary;
  }

  get fileName(): string {
    const attachment = this.resource.content?.[0]?.attachment;
    const fromUrl = attachment?.url?.split("/").pop()?.split("?")[0];
    return attachment?.title || fromUrl || "Unnamed Document";
  }

  get contentType(): string {
    return this.resource.content?.[0]?.attachment?.contentType || "application/octet-stream";
  }

  get fileUrl(): string | undefined {
    return this.resource.content?.[0]?.attachment?.url;
  }

  get date(): string | undefined {
    return this.resource.date;
  }

  get metaLastUpdated(): string | undefined {
    return this.resource.meta?.lastUpdated;
  }

  get identifiers(): Identifier[] | undefined {
    return this.resource.identifier;
  }

  /**
   * Get all Binary resource IDs referenced by this document.
   *
   * @returns Array of Binary resource IDs
   */
  get binaryIds(): string[] {
    const ids: string[] = [];

    // Medplum can surface attachments as stable `Binary/<id>` refs or presigned storage URLs.
    for (const attachment of this.attachments) {
      const binaryUrl = attachment.url;
      if (!binaryUrl) {
        continue;
      }

      const binaryId = resolveBinaryIdFromUrl(binaryUrl);
      if (binaryId) {
        ids.push(binaryId);
      }
    }

    return ids;
  }

  //------------------------------------------------------------------------------------------------
  //                                 STATIC METHODS
  //------------------------------------------------------------------------------------------------
  /**
   * Get a document by its id.
   *
   * @param medplum - The Medplum client.
   * @param id - The id of the document.
   * @returns The document.
   */
  static async getById(medplum: MedplumClient, id: string) {
    const resource = await medplum.searchOne(Document.ResourceType, {
      _id: id,
    });
    return resource ? new Document(resource) : undefined;
  }

  /**
   * Get a document by its reference.
   *
   * @param medplum - The Medplum client.
   * @param reference - The reference to the document.
   * @returns The document.
   */
  static async getByReference(medplum: MedplumClient, reference: string) {
    const [resourceType, id] = reference.split("/");
    if (resourceType !== Document.ResourceType) throw new Error("Invalid reference");
    return await Document.getById(medplum, id);
  }

  /**
   * Create a new document reference resource.
   *
   * @param medplum - The Medplum client.
   * @param request - The document request.
   * @returns The created document reference.
   */
  static async create(
    medplum: MedplumClient,
    request: DocumentData,
    options?: CreateResourceOptions,
  ) {
    const documentReference = await Document.createResource(
      medplum,
      Document.toFhirCreateObject(request),
      options,
    );

    return new Document(documentReference);
  }

  /**
   * Creates a document reference resource only if no existing resource matches the
   * conditional query — atomic idempotency (see FHIR conditional create), unlike a
   * search-then-create check which races under concurrent callers.
   *
   * @param medplum - The Medplum client.
   * @param request - The document request.
   * @param ifNoneExistQuery - FHIR search query string identifying the existing resource to match.
   * @returns The existing or newly created document reference.
   */
  static async createIfNoneExist(
    medplum: MedplumClient,
    { request, ifNoneExistQuery, options }: DocumentCreateIfNoneExistParams,
  ) {
    const documentReference = await Document.createResourceIfNoneExist(medplum, {
      resource: Document.toFhirCreateObject(request),
      ifNoneExistQuery,
      options,
    });

    return new Document(documentReference);
  }

  private static toFhirCreateObject(request: DocumentData) {
    return withDocumentReferenceSortPeriod({
      status: request.status ?? "current",
      docStatus: request.docStatus ?? "final",
      description: request.description,
      content: request.attachments.map((attachment) => ({
        attachment,
      })),
      identifier: request.identifiers,
      date: request.date ?? time().toISOString(),
      subject: request.subject,
      custodian: request.custodian,
      author: request.author,
      extension: request.extension,
      type: request.type,
      context: request.context,
      meta: request.meta,
      category: request.category,
    });
  }

  /**
   * Creates a DocumentReference resource from raw file content.
   * Returns the document and whether it was newly created (for idempotency).
   */
  static async createFromFile(
    medplum: MedplumClient,
    request: CreateDocumentFromFileRequest,
    options?: CreateDocumentFromFileOptions,
  ): Promise<CreateDocumentFromFileResult> {
    return createDocumentFromFile({
      documentModel: {
        createDocument: (resource) => new Document(resource),
        createResource: (client, resource, createOptions) =>
          Document.createResource(client, resource, createOptions),
        createResourceIfNoneExist: (client, params) =>
          Document.createResourceIfNoneExist(client, params),
      },
      medplum,
      request,
      options,
    });
  }

  /**
   * Creates a DocumentReference for a Binary the browser already uploaded. With an identity the
   * create is conditional, and `wasCreated` reports whether this call's Binary won.
   */
  static async createFromUploadedBinary(
    medplum: MedplumClient,
    request: CreateDocumentFromBinaryRequest,
    options?: CreateResourceOptions,
  ): Promise<CreateDocumentFromBinaryResult> {
    return createDocumentFromUploadedBinary({
      documentModel: {
        createDocument: (resource) => new Document(resource),
        createResource: (client, resource, createOptions) =>
          Document.createResource(client, resource, createOptions),
        createResourceIfNoneExist: (client, params) =>
          Document.createResourceIfNoneExist(client, params),
      },
      medplum,
      request,
      options,
    });
  }

  /**
   * Documents by id, optionally scoped to one patient. One search per 100 ids (the query-string
   * limit). With no `ids`, a plain search on `subject`, as the base class runs. An empty `ids` list
   * reads nothing. Pair with {@link Document.subscribe} on the same `subject` through
   * `useModelSearchSubscription`, which refetches when any of the patient's documents changes.
   */
  static async search(
    medplum: MedplumClient,
    query: DocumentSearchQuery,
    options?: SearchOptions,
  ): Promise<Document[]> {
    const subject = query.subject ? { subject: query.subject } : {};
    // no ids: the plain search the base class runs, so a subject-only caller still gets results
    if (!query.ids) {
      const resources = await Document.searchResources(medplum, subject, options);
      return resources.map((resource) => new Document(resource));
    }
    const ids = query.ids;
    const documents: Document[] = [];
    for (let start = 0; start < ids.length; start += MAX_IDS_PER_SEARCH) {
      const chunk = ids.slice(start, start + MAX_IDS_PER_SEARCH);
      const resources = await Document.searchResources(
        medplum,
        { ...subject, _id: chunk.join(",") },
        { ...options, limit: chunk.length },
      );
      documents.push(...resources.map((resource) => new Document(resource)));
    }
    return documents;
  }

  /**
   * Searches for a single DocumentReference and returns it as a model instance.
   */
  static async searchOne(
    medplum: MedplumClient,
    query: Record<string, unknown>,
    options?: SearchOptions,
  ): Promise<Document | undefined> {
    const resource = await Document.searchOneResource(medplum, query, options);
    return resource ? new Document(resource) : undefined;
  }

  /**
   * One page of the practice's DocumentReference changes in compound `(_lastUpdated, _id)`
   * order — the query the API v2 webhook drain runs for `document.changed`, and the same
   * `_lastUpdated` range read bulk export's `_since` already performs.
   *
   * Reads run on the compartment-scoped serving client, so a bare search already returns
   * only the practice's documents with quarantined candidates excluded; there is no
   * practice predicate here on purpose.
   *
   * The `_id` tie-break is mandatory and pairs with the caller's client-side tie skip
   * (`roster-feed.ts`): same-millisecond timestamps are real, and a `ge` bound alone would
   * re-deliver the boundary record forever. Never paginate this by following the Bundle's
   * `next` link — those are offset-based and skip records under concurrent writes.
   *
   * @param medplum - The Medplum client.
   * @param query - `updatedOnOrAfter` (inclusive ISO lower bound) and the page `count`.
   * @returns The page, ascending by `(lastUpdated, id)`.
   */
  static async searchChanges(
    medplum: MedplumClient,
    query: { updatedOnOrAfter?: string; count: number },
  ): Promise<Document[]> {
    const bundle: Bundle = await medplum.search(Document.ResourceType, {
      _sort: "_lastUpdated,_id",
      _count: query.count,
      ...(query.updatedOnOrAfter ? { _lastUpdated: `ge${query.updatedOnOrAfter}` } : {}),
    });

    return (bundle.entry ?? [])
      .map((entry) => entry.resource)
      .filter(
        (resource): resource is DocumentReference =>
          resource?.resourceType === Document.ResourceType && !!resource.id,
      )
      .map((resource) => new Document(resource));
  }

  /**
   * Finds a document by identifier system/value pair.
   */
  static async findByIdentifier(
    medplum: MedplumClient,
    system: string,
    value: string,
  ): Promise<Document | undefined> {
    return Document.searchOne(medplum, { identifier: `${system}|${value}` });
  }

  /**
   * Subscribe to document changes for this model.
   *
   * @param medplum - The Medplum client.
   * @param query - The query parameters for filtering documents.
   * @param callback - The callback function to handle updates.
   * @returns The subscription object.
   */
  static subscribe(
    medplum: MedplumClient,
    query: DocumentSearchQuery,
    callback: (document: Document, metadata: SubscriptionEventMetadata) => void,
  ) {
    const fhirQuery: Record<string, string> = {};
    if (query.subject) {
      fhirQuery.subject = query.subject;
    }

    return Document.subscribeToResource(medplum, fhirQuery, (resource, metadata) => {
      callback(new Document(resource), metadata);
    });
  }

  /**
   * Get recently uploaded documents for a patient.
   *
   * @param medplum - The Medplum client.
   * @param patientId - The patient's ID.
   * @param options - Query options.
   * @returns Array of Document instances, newest first.
   *
   * @note When `includePending` is false (default), the method fetches up to `limit` documents
   * from the API and then filters locally by parsing status. This means fewer than `limit`
   * documents may be returned if some are still processing. Set `includePending: true` to
   * ensure the full `limit` count is returned regardless of parsing status.
   */
  static async getRecentByPatient(
    medplum: MedplumClient,
    patientId: string,
    options?: {
      /** Max documents to return (default 10). See @note about filtering behavior. */
      limit?: number;
      /** How far back to look in days (default 30) */
      days?: number;
      /** Include documents still being parsed (default false). When false, may return fewer than `limit` documents. */
      includePending?: boolean;
    },
  ): Promise<Document[]> {
    const limit = options?.limit ?? 10;
    const days = options?.days ?? 30;
    const includePending = options?.includePending ?? false;

    const cutoffDate = time().subtract(days, "days").toISOString();

    const resources = await Document.searchResources(medplum, {
      subject: `Patient/${patientId}`,
      date: `ge${cutoffDate}`,
      _sort: "-date",
      _count: limit,
    });

    const documents = resources.map((r) => new Document(r));

    // Filter by parsing status if not including pending
    if (!includePending) {
      return documents.filter((d) => d.parsingStatus === "completed");
    }

    return documents;
  }

  /**
   * Delete a document and all its associated Binary resources.
   *
   * Writes more than once: one delete per Binary, then one for the DocumentReference.
   * A Binary that fails to delete is logged and skipped, so a partial outcome is possible.
   *
   * Idempotent. A document that is already gone reports `alreadyDeleted` instead of throwing,
   * because a delete that finds nothing left to delete has reached its goal. Without that, a
   * retry after a partial delete can never converge and the clinician has no way to finish.
   *
   * Resolves through `readResource` rather than `Document.getById`. `getById` searches, and a
   * FHIR search never returns a soft-deleted resource, so it cannot tell "deleted" from "never
   * existed". `getById` keeps its search semantics for every other caller.
   *
   * @param medplum - The Medplum client.
   * @param documentId - The ID of the document to delete.
   * @returns Deleted Binary IDs, the member the document belonged to, and whether it was already gone
   */
  static async deleteWithBinaries(medplum: MedplumClient, documentId: string) {
    let document: Document;
    try {
      document = new Document(await medplum.readResource(Document.ResourceType, documentId));
    } catch (error) {
      if (!isMedplumResourceMissingError(error)) {
        throw error;
      }

      logger.info("Document already deleted, reporting delete as complete", { documentId });
      return {
        documentId,
        deletedBinaries: [] as string[],
        memberId: undefined,
        alreadyDeleted: true as const,
      };
    }

    const deletedBinaries: string[] = [];

    // Delete all associated Binary resources
    for (const binaryId of document.binaryIds) {
      try {
        await MedplumFile.delete(medplum, binaryId);
        deletedBinaries.push(binaryId);
      } catch (error) {
        // Log error but continue with deletion
        logger.warn("Failed to delete binary while deleting document", {
          ...extractTelemetryErrorMetadata({ error }),
          binaryId,
          documentId,
          memberId: document.memberId,
        });
      }
    }

    // Delete the DocumentReference itself
    await document.delete(medplum);

    return {
      documentId,
      deletedBinaries,
      memberId: document.memberId,
      alreadyDeleted: false as const,
    };
  }

  //------------------------------------------------------------------------------------------------
  //                                 INSTANCE METHODS
  //------------------------------------------------------------------------------------------------
  /**
   * Update the filename (attachment title) of the document.
   *
   * @param medplum - The Medplum client.
   * @param fileName - The new filename.
   * @returns The updated document instance.
   */
  async updateFileName(medplum: MedplumClient, fileName: string): Promise<Document> {
    if (!this.resource.content?.[0]) {
      throw new Error("Document has no content");
    }

    if (!this.id) {
      throw new Error("Document has no ID");
    }

    // Construct the updated content array with the new filename
    const updatedContent = [
      {
        ...this.resource.content[0],
        attachment: {
          ...this.resource.content[0].attachment,
          title: fileName,
        },
      },
      // Include any additional content items if they exist
      ...(this.resource.content.slice(1) || []),
    ];

    // Use the centralized update helper from FhirResource
    const updatedResource = await Document.patchResource(medplum, {
      id: this.id,
      patch: {
        content: updatedContent,
      },
    });

    return new Document(updatedResource);
  }

  /**
   * Points the document's file at a new Binary, e.g. the same file after a rotate. The title and
   * the content type stay. The size changes, and the old hash no longer matches, so it goes.
   * Pinned to the version the caller read, so a concurrent edit raises a conflict instead of being
   * overwritten.
   */
  async replaceFile(
    medplum: MedplumClient,
    { binaryId, size }: { binaryId: string; size: number },
  ): Promise<Document> {
    const [first, ...rest] = this.resource.content ?? [];
    if (!first) throw new Error("Document has no content");
    if (!this.id) throw new Error("Document has no ID");

    const { hash: _hash, ...attachment } = first.attachment;
    const updatedResource = await Document.patchResource(medplum, {
      id: this.id,
      patch: {
        content: [
          { ...first, attachment: { ...attachment, url: `Binary/${binaryId}`, size } },
          ...rest,
        ],
      },
      options: { ifMatchVersionId: this.resource.meta?.versionId },
    });
    return new Document(updatedResource);
  }

  /**
   * Update the description and attachment title of the document.
   * This is used to update fax document names when the sender display is changed.
   *
   * @param medplum - The Medplum client.
   * @param description - The new description/title for the document.
   * @returns The updated document instance.
   */
  async updateDescription(medplum: MedplumClient, description: string): Promise<Document> {
    if (!this.id) {
      throw new Error("Document has no ID");
    }

    const [content, ...otherContent] = this.resource.content || [];
    const updatedResource = await Document.patchResource(medplum, {
      id: this.id,
      patch: {
        description,
        content: content
          ? [
              {
                ...content,
                ...(content.attachment
                  ? {
                      attachment: {
                        ...content.attachment,
                        title: description,
                      },
                    }
                  : {}),
              },
              ...otherContent,
            ]
          : undefined,
      },
    });
    return new Document(updatedResource);
  }

  /**
   * Update the clinical service date used by External Records chronology.
   */
  async updateServiceDate(
    medplum: MedplumClient,
    serviceDate: Date | string | null,
  ): Promise<Document> {
    if (!this.id) {
      throw new Error("Document has no ID");
    }

    const serviceDatePatch = serviceDate
      ? buildDocumentServiceDateFields({
          serviceDate,
          source: "manual",
          context: this.resource.context,
        })
      : undefined;
    const serviceDateIso = serviceDatePatch?.extension.find(
      (extension) => extension.url === EFFECTIVE_DATE_EXTENSION_URL,
    )?.valueString;
    const existingDateIso = this.effectiveDate
      ? normalizeServiceDateToUtcIso(this.effectiveDate)
      : undefined;
    const fallbackDate = this.date ?? this.metaLastUpdated;
    const clearPatch = serviceDate
      ? undefined
      : buildDocumentServiceDateClearPatch({
          context: this.resource.context,
          fallbackDate,
        });
    const nextContext = serviceDatePatch?.context ?? clearPatch?.context;
    const nextPeriodStart = serviceDateIso ?? fallbackDate;

    if (
      serviceDateIso === existingDateIso &&
      (serviceDateIso ? this.effectiveDateSource === "manual" : !this.effectiveDateSource) &&
      this.resource.context?.period?.start === nextPeriodStart
    ) {
      return this;
    }

    const updatedResource = await Document.patchResource(medplum, {
      id: this.id,
      patch: {
        extension: serviceDatePatch?.extension ?? clearPatch?.extension,
        context: nextContext ?? null,
      },
    });

    return new Document(updatedResource);
  }

  /**
   * Compatibility wrapper for older call sites that still use the effective-date name.
   */
  async updateEffectiveDate(
    medplum: MedplumClient,
    effectiveDate: Date | string,
  ): Promise<Document> {
    return this.updateServiceDate(medplum, effectiveDate);
  }
}

function normalizeDateOnlyToUtcIso(dateOnly: string): string {
  const parsed = parseDate(dateOnly);
  if (!parsed.isValid()) {
    throw new Error("Invalid service date");
  }

  return time.utc(parsed.format("YYYY-MM-DD")).toISOString();
}

function buildContextWithPeriodStart({
  context,
  periodStart,
}: {
  context: DocumentReference["context"] | undefined;
  periodStart: string | undefined;
}): DocumentReference["context"] | undefined {
  const { period, ...contextWithoutPeriod } = context ?? {};
  const nextPeriod = { ...period };

  if (periodStart) {
    nextPeriod.start = periodStart;
  } else {
    delete nextPeriod.start;
  }

  const hasPeriod = Object.keys(nextPeriod).length > 0;
  const nextContext = {
    ...contextWithoutPeriod,
    ...(hasPeriod ? { period: nextPeriod } : {}),
  };

  return Object.keys(nextContext).length > 0 ? nextContext : undefined;
}
